import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { Button } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CopilotContextComposerService } from '@brighthub/copilot';
import { FilterChipGroupComponent } from '../shared/filter-chip-group/filter-chip-group.component';
import { ColResizeDirective } from './col-resize.directive';
import { OutcomesStateService } from './outcomes-state.service';
import { Outcome, OutcomeFilter, OutcomesState } from './outcomes.model';
import { OutcomeAddDialogComponent } from './outcome-add-dialog/outcome-add-dialog.component';
import { OutcomeDetailsDialogComponent, OutcomeDialogResult } from './outcome-details-dialog/outcome-details-dialog.component';

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const ATI_BASIS_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  annual: 'Annual',
  one_time: 'One-time',
};

const ALL_FILTERS: { id: OutcomeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'verified', label: 'Verified' },
  { id: 'unverified', label: 'Unverified' },
  { id: 'ai', label: 'AI' },
  { id: 'human', label: 'Human' },
  { id: 'has_ati', label: 'Has ATI' },
  { id: 'no_ati', label: 'No ATI' },
];

const EMPTY_STATE: OutcomesState = { outcomes: [] };

@Component({
  selector: 'bai-outcomes',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button, ColResizeDirective, FilterChipGroupComponent, TagModule],
  providers: [DialogService],
  templateUrl: './outcomes.component.html',
  styleUrl: './outcomes.component.scss',
})
export class OutcomesComponent {
  private readonly _stateService = inject(OutcomesStateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _copilotComposer = inject(CopilotContextComposerService);
  private _dialogRef: DynamicDialogRef | null = null;

  readonly sessionId = input<string>('');

  readonly allFilters = ALL_FILTERS;
  readonly activeFilter = signal<OutcomeFilter>('all');

  constructor() {
    effect(() => {
      const id = this.sessionId();
      if (id) this._stateService.ensureState(id);
    });
  }

  private readonly _state = computed<OutcomesState>(() => {
    const id = this.sessionId();
    if (!id) return EMPTY_STATE;
    return this._stateService.storeSnapshot()[id] ?? EMPTY_STATE;
  });

  readonly filteredOutcomes = computed<Outcome[]>(() => {
    const outcomes = this._state().outcomes;
    const filter = this.activeFilter();
    switch (filter) {
      case 'verified':   return outcomes.filter((o) => o.status === 'verified');
      case 'unverified': return outcomes.filter((o) => o.status === 'unverified');
      case 'ai':         return outcomes.filter((o) => o.sourceType === 'ai');
      case 'human':      return outcomes.filter((o) => o.sourceType === 'human');
      case 'has_ati':    return outcomes.filter((o) => o.atiValue !== null);
      case 'no_ati':     return outcomes.filter((o) => o.atiValue === null);
      default:           return outcomes;
    }
  });

  setFilter(filter: OutcomeFilter): void {
    this.activeFilter.set(filter);
  }

  setFilterSelection(selection: string | string[]): void {
    if (Array.isArray(selection)) return;
    if (this.allFilters.some((filter) => filter.id === selection)) {
      this.setFilter(selection as OutcomeFilter);
    }
  }

  formatAti(value: number | null): string {
    return value !== null ? USD.format(value) : '—';
  }

  formatBasis(basis: string | null): string {
    return basis ? (ATI_BASIS_LABELS[basis] ?? basis) : '—';
  }

  openDetails(outcome: Outcome): void {
    this._dialogRef = this._dialogService.open(OutcomeDetailsDialogComponent, {
      header: 'Outcome Details',
      width: '520px',
      data: outcome,
    });
    this._dialogRef.onClose.subscribe((result: OutcomeDialogResult | null) => {
      if (result) {
        const id = this.sessionId();
        if (id) {
          this._stateService.updateOutcome(id, outcome.id, {
            ...result.patch,
            updatedBy: result.currentUser,
          });
        }
      }
    });
  }

  openAdd(): void {
    this._dialogRef = this._dialogService.open(OutcomeAddDialogComponent, {
      header: 'Add Outcome',
      width: '560px',
    });
    this._dialogRef.onClose.subscribe((result) => {
      if (result) {
        const id = this.sessionId();
        if (id) this._stateService.addOutcome(id, result);
      }
    });
  }

  addOutcomeToCopilot(outcome: Outcome): void {
    const preview = this.truncate(outcome.description, 20);
    this._copilotComposer.addContext({
      sourceType: 'actions-outcome',
      sourceId: outcome.id,
      label: `Outcome: ${preview}`,
      fullText:
        `[Actions | Outcome | ${outcome.id}] ` +
        `Description: ${outcome.description}; ` +
        `Tags: ${outcome.tags.length > 0 ? outcome.tags.join(', ') : '—'}; ` +
        `Source Type: ${outcome.sourceType}; Date Achieved: ${outcome.dateAchieved || '—'}; ` +
        `ATI Value: ${this.formatAti(outcome.atiValue)}; ATI Basis: ${this.formatBasis(outcome.atiBasis)}; ` +
        `Status: ${outcome.status}; Linked Action: ${outcome.linkedActionId || '—'}; ` +
        `Time to Outcome: ${outcome.timeToOutcomeDays !== null ? `${outcome.timeToOutcomeDays} days` : '—'}; ` +
        `Updated By: ${outcome.updatedBy}; Updated At: ${outcome.updatedAt}`,
    });
  }

  truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }
}
